import { gql, useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useRef } from 'react';
import EmptyState from '../../components/EmptyState';
import type {
  CommentHistory_PostFragment,
  HistoricalThreadsQuery,
  HistoricalThreadsQueryVariables,
  UnresolveThreadMutation,
  UnresolveThreadMutationVariables,
} from '../../graphql/generated/graphqlTypes';
import THREAD_UNRESOLVED_SUBSCRIPTION from '../../graphql/threadUnresolved';
import UNRESOLVE_THREAD_MUTATION from '../../graphql/unresolveThread';
import UIActions from '../../ui/UIActions';
import updateSubscribedList from '../../utils/graphql/updateSubscribedList';
import groupByCutoffs from '../../utils/groupByCutoffs';
import commentThreadHash from '../../utils/url/commentThreadHash';
import postPath from '../../utils/url/postPath';
import { useLocation, useRouter } from '../../utils/router/v3Bridge';
import getThreadIdFromUrlHash from '../getThreadIdFromUrlHash';
import historyCutoffs from '../history/historyCutoffs';
import styles from './CommentHistory.module.scss';
import handleUnresolveThreadError from './handleUnresolveThreadError';
import HistoricalCommentThread from './HistoricalCommentThread';
import { useAppDispatch } from '../../app/redux/useAppSelector';

const POST_FRAGMENT = gql`
  fragment CommentHistory_post on Post {
    id
    ...postPath_post
  }
  ${postPath.fragments.post}
`;

const HISTORICAL_THREADS_QUERY = gql`
  query historicalThreads($postId: ID!) {
    post(id: $postId) {
      id
      threads(historical: true) {
        id
        ...HistoricalCommentThread_thread
      }
    }
  }
  ${HistoricalCommentThread.fragments.thread}
`;

interface CommentHistorySidebarProps {
  post: CommentHistory_PostFragment;
}

const CommentHistory = ({ post }: CommentHistorySidebarProps) => {
  const router = useRouter();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { id: postId } = post;
  const { loading, data, subscribeToMore } = useQuery<
    HistoricalThreadsQuery,
    HistoricalThreadsQueryVariables
  >(HISTORICAL_THREADS_QUERY, {
    fetchPolicy: 'network-only',
    variables: { postId },
  });

  useEffect(
    () =>
      subscribeToMore?.({
        document: THREAD_UNRESOLVED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList('post.threads', 'thread', false),
      }),
    [subscribeToMore, postId],
  );

  const threads = data?.post ? data.post.threads : [];

  const targetRef = useRef<HTMLDivElement | null>(null);
  const targetThreadIdRef = useRef(getThreadIdFromUrlHash(location.hash));

  useEffect(() => {
    if (!targetThreadIdRef.current || loading) return;

    if (targetRef.current) {
      targetRef.current.scrollIntoView();
    } else {
      dispatch(
        UIActions.pushToast({
          level: 'error',
          message: 'That comment thread no longer exists.',
        }),
      );
    }
  }, [dispatch, loading]);

  const [unresolveThread] = useMutation<
    UnresolveThreadMutation,
    UnresolveThreadMutationVariables
  >(UNRESOLVE_THREAD_MUTATION);

  if (!loading && threads.length === 0) {
    return (
      <EmptyState
        title="No comment history"
        message="All resolved and deleted comments will appear here."
        vertical
      />
    );
  }

  const cutoffs = historyCutoffs();
  return (
    <>
      {groupByCutoffs(cutoffs, threads, 'resolvedAt').map(
        ({ items, key, label }) => (
          <div className={styles.threadWrapper} key={key}>
            {label && <div className={styles.sectionHeader}>{label}</div>}
            {items.map((item) => (
              <div
                key={item.id}
                ref={
                  item.id === targetThreadIdRef.current ? targetRef : undefined
                }
              >
                <HistoricalCommentThread
                  thread={item}
                  onUnresolve={() =>
                    unresolveThread({ variables: { id: item.id } })
                      .then(() => {
                        const url = `${postPath(post)}${commentThreadHash(
                          item.id,
                        )}`;
                        router.push(url);
                      })
                      .catch((e) => {
                        handleUnresolveThreadError(e, dispatch);
                        throw e;
                      })
                  }
                />
              </div>
            ))}
          </div>
        ),
      )}
    </>
  );
};

CommentHistory.fragments = { post: POST_FRAGMENT };

export default CommentHistory;
